import React from 'react';
import { useIntl } from 'react-intl-next';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import { toggleBulletList as toggleBulletListKeymap, toggleOrderedList as toggleOrderedListKeymap, toggleTaskItemCheckbox as toggleTaskItemCheckboxKeymap, toggleTaskList as toggleTaskListKeymap, formatShortcut, ToolTipContent } from '@atlaskit/editor-common/keymaps';
import { listMessages, tasksAndDecisionsMessages } from '@atlaskit/editor-common/messages';
import { getInputMethodFromParentKeys } from '@atlaskit/editor-common/toolbar';
import { ListBulletedIcon, ListNumberedIcon, ToolbarButton, ToolbarTooltip } from '@atlaskit/editor-toolbar';
import TaskIcon from '@atlaskit/icon/core/task';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
function useListsIndentationHeroButtonInfo(_ref) {
  var api = _ref.api,
    parents = _ref.parents,
    defaultListType = _ref.defaultListType;
  var _useIntl = useIntl(),
    formatMessage = _useIntl.formatMessage;
  var _useSharedPluginState = useSharedPluginStateWithSelector(api, ['list', 'taskDecision'], function (states) {
      var _states$listState, _states$listState2, _states$listState3, _states$taskDecisionS;
      return {
        bulletListActive: (_states$listState = states.listState) === null || _states$listState === void 0 ? void 0 : _states$listState.bulletListActive,
        bulletListDisabled: (_states$listState2 = states.listState) === null || _states$listState2 === void 0 ? void 0 : _states$listState2.bulletListDisabled,
        orderedListActive: (_states$listState3 = states.listState) === null || _states$listState3 === void 0 ? void 0 : _states$listState3.orderedListActive,
        taskListActive: (_states$taskDecisionS = states.taskDecisionState) === null || _states$taskDecisionS === void 0 ? void 0 : _states$taskDecisionS.isInsideTask
      };
    }),
    bulletListActive = _useSharedPluginState.bulletListActive,
    bulletListDisabled = _useSharedPluginState.bulletListDisabled,
    orderedListActive = _useSharedPluginState.orderedListActive,
    taskListActive = _useSharedPluginState.taskListActive;
  var getListType = taskListActive ? 'taskList' : orderedListActive ? 'orderedList' : defaultListType;
  var taskListKeymap = expValEquals('platform_editor_toolbar_aifc_patch_6', 'isEnabled', true) ? toggleTaskListKeymap : toggleTaskItemCheckboxKeymap;
  var getKeymap = getListType === 'taskList' ? taskListKeymap : getListType === 'orderedList' ? toggleOrderedListKeymap : toggleBulletListKeymap;
  var shortcut = formatShortcut(getKeymap);
  var keymap = getKeymap;
  var message = getListType === 'taskList' ? formatMessage(tasksAndDecisionsMessages.taskList) : getListType === 'orderedList' ? formatMessage(listMessages.orderedList) : formatMessage(listMessages.bulletedList);
  var onClick = function onClick() {
    var inputMethod = getInputMethodFromParentKeys(parents);
    if (getListType === 'taskList') {
      var _api$taskDecision;
      api === null || api === void 0 || api.core.actions.execute(api === null || api === void 0 || (_api$taskDecision = api.taskDecision) === null || _api$taskDecision === void 0 ? void 0 : _api$taskDecision.commands.toggleTaskList());
    } else if (getListType === 'orderedList') {
      api === null || api === void 0 || api.core.actions.execute(api === null || api === void 0 ? void 0 : api.list.commands.toggleOrderedList(inputMethod));
    } else {
      api === null || api === void 0 || api.core.actions.execute(api === null || api === void 0 ? void 0 : api.list.commands.toggleBulletList(inputMethod));
    }
  };
  var iconBefore = getListType === 'taskList' ? /*#__PURE__*/React.createElement(TaskIcon, {
    label: formatMessage(tasksAndDecisionsMessages.taskList),
    size: "small"
  }) : getListType === 'orderedList' ? /*#__PURE__*/React.createElement(ListNumberedIcon, {
    label: formatMessage(listMessages.orderedList),
    size: "small"
  }) : /*#__PURE__*/React.createElement(ListBulletedIcon, {
    label: formatMessage(listMessages.bulletedList),
    size: "small"
  });
  var isSelected = getListType === 'bulletList' ? bulletListActive : getListType === 'orderedList' ? orderedListActive : taskListActive;
  var isDisabled = !orderedListActive && !taskListActive && bulletListDisabled;
  return {
    shortcut: shortcut,
    keymap: keymap,
    message: message,
    onClick: onClick,
    iconBefore: iconBefore,
    isSelected: isSelected,
    isDisabled: isDisabled
  };
}
export var ListsIndentationHeroButtonCollapsed = function ListsIndentationHeroButtonCollapsed(_ref2) {
  var api = _ref2.api,
    parents = _ref2.parents;
  var _useListsIndentationH = useListsIndentationHeroButtonInfo({
      api: api,
      parents: parents,
      defaultListType: 'bulletList'
    }),
    shortcut = _useListsIndentationH.shortcut,
    keymap = _useListsIndentationH.keymap,
    message = _useListsIndentationH.message,
    onClick = _useListsIndentationH.onClick,
    iconBefore = _useListsIndentationH.iconBefore,
    isSelected = _useListsIndentationH.isSelected,
    isDisabled = _useListsIndentationH.isDisabled;
  return /*#__PURE__*/React.createElement(ToolbarTooltip, {
    content: /*#__PURE__*/React.createElement(ToolTipContent, {
      description: message,
      keymap: keymap
    })
  }, /*#__PURE__*/React.createElement(ToolbarButton, {
    iconBefore: iconBefore,
    isSelected: isSelected,
    isDisabled: isDisabled,
    ariaKeyshortcuts: shortcut,
    onClick: onClick
  }));
};
export var ListsIndentationHeroButtonNew = function ListsIndentationHeroButtonNew(_ref3) {
  var api = _ref3.api,
    parents = _ref3.parents;
  var _useListsIndentationH2 = useListsIndentationHeroButtonInfo({
      api: api,
      parents: parents,
      defaultListType: 'orderedList'
    }),
    shortcut = _useListsIndentationH2.shortcut,
    keymap = _useListsIndentationH2.keymap,
    message = _useListsIndentationH2.message,
    onClick = _useListsIndentationH2.onClick,
    iconBefore = _useListsIndentationH2.iconBefore,
    isSelected = _useListsIndentationH2.isSelected,
    isDisabled = _useListsIndentationH2.isDisabled;
  return /*#__PURE__*/React.createElement(ToolbarTooltip, {
    content: /*#__PURE__*/React.createElement(ToolTipContent, {
      description: message,
      keymap: keymap
    })
  }, /*#__PURE__*/React.createElement(ToolbarButton, {
    iconBefore: iconBefore,
    isSelected: isSelected,
    isDisabled: isDisabled,
    ariaKeyshortcuts: shortcut,
    onClick: onClick
  }));
};
export var ListsIndentationHeroButton = function ListsIndentationHeroButton(_ref4) {
  var api = _ref4.api,
    parents = _ref4.parents;
  var _useIntl2 = useIntl(),
    formatMessage = _useIntl2.formatMessage;
  var _useSharedPluginState2 = useSharedPluginStateWithSelector(api, ['list', 'taskDecision'], function (states) {
      var _states$listState4, _states$listState5, _states$listState6, _states$taskDecisionS2;
      return {
        bulletListActive: (_states$listState4 = states.listState) === null || _states$listState4 === void 0 ? void 0 : _states$listState4.bulletListActive,
        bulletListDisabled: (_states$listState5 = states.listState) === null || _states$listState5 === void 0 ? void 0 : _states$listState5.bulletListDisabled,
        orderedListActive: (_states$listState6 = states.listState) === null || _states$listState6 === void 0 ? void 0 : _states$listState6.orderedListActive,
        taskListActive: (_states$taskDecisionS2 = states.taskDecisionState) === null || _states$taskDecisionS2 === void 0 ? void 0 : _states$taskDecisionS2.isInsideTask
      };
    }),
    bulletListActive = _useSharedPluginState2.bulletListActive,
    bulletListDisabled = _useSharedPluginState2.bulletListDisabled,
    orderedListActive = _useSharedPluginState2.orderedListActive,
    taskListActive = _useSharedPluginState2.taskListActive;
  var taskListKeymap = expValEquals('platform_editor_toolbar_aifc_patch_6', 'isEnabled', true) ? toggleTaskListKeymap : toggleTaskItemCheckboxKeymap;
  var shortcut = taskListActive ? formatShortcut(taskListKeymap) : orderedListActive ? formatShortcut(toggleOrderedListKeymap) : formatShortcut(toggleBulletListKeymap);
  var onClick = function onClick() {
    var inputMethod = getInputMethodFromParentKeys(parents);
    if (taskListActive) {
      var _api$taskDecision2;
      api === null || api === void 0 || api.core.actions.execute(api === null || api === void 0 || (_api$taskDecision2 = api.taskDecision) === null || _api$taskDecision2 === void 0 ? void 0 : _api$taskDecision2.commands.toggleTaskList());
    } else if (orderedListActive) {
      api === null || api === void 0 || api.core.actions.execute(api === null || api === void 0 ? void 0 : api.list.commands.toggleOrderedList(inputMethod));
    } else {
      api === null || api === void 0 || api.core.actions.execute(api === null || api === void 0 ? void 0 : api.list.commands.toggleBulletList(inputMethod));
    }
  };
  return /*#__PURE__*/React.createElement(ToolbarTooltip, {
    content: taskListActive ? formatMessage(tasksAndDecisionsMessages.taskList) : orderedListActive ? formatMessage(listMessages.orderedList) : formatMessage(listMessages.bulletedList)
  }, /*#__PURE__*/React.createElement(ToolbarButton, {
    iconBefore: taskListActive ? /*#__PURE__*/React.createElement(TaskIcon, {
      label: formatMessage(tasksAndDecisionsMessages.taskList),
      size: "small"
    }) : orderedListActive ? /*#__PURE__*/React.createElement(ListNumberedIcon, {
      label: formatMessage(listMessages.orderedList),
      size: "small"
    }) : /*#__PURE__*/React.createElement(ListBulletedIcon, {
      label: formatMessage(listMessages.bulletedList),
      size: "small"
    }),
    isSelected: bulletListActive || orderedListActive || taskListActive,
    isDisabled: !orderedListActive && !taskListActive && bulletListDisabled,
    ariaKeyshortcuts: shortcut,
    onClick: onClick
  }));
};